// Copyright (c) 2020-2021. Sendanor <info@sendanor.fi>. All rights reserved.

import React from 'react';
import './LoginView.scss';
import Form from "../../../../nor/ui/components/form/Form";
import FormModel from "../../../../nor/ui/types/FormModel";
import FormItemType from "../../../../nor/ui/types/FormItemType";
import { ChangeCallback, VoidCallback } from "../../../../nor/ts/interfaces/callbacks";
import FormAppClassName from "../../../../constants/FormAppClassName";
import SessionService, {
    SessionServiceDestructor,
    SessionServiceEvent
} from "../../../../services/SessionService";
import FormLoginModal from '../../../../modals/FormLoginModal';
import LogService from "../../../../nor/ts/LogService";

const LOG = LogService.createLogger('LoginView');

export interface LoginFormData {
    username : string;
    password : string;
}

export interface LoginViewState {

    readonly form       : LoginFormData;
    readonly loginError : boolean;
    readonly submitting : boolean;
    readonly hasSession : boolean;

}

export interface LoginViewProps {
    readonly className  ?: string;
    readonly hasSession ?: boolean | undefined;
}

export class LoginView extends React.Component<LoginViewProps, LoginViewState> {

    private readonly _loginFormModel         : FormModel;
    private readonly _changeFormCallback     : ChangeCallback<LoginFormData>;
    private readonly _submitFormCallback     : VoidCallback;
    private readonly _loginErrorCallback     : VoidCallback;
    private readonly _sessionChangedCallback : VoidCallback;

    private _loginErrorListener    : SessionServiceDestructor | undefined;
    private _sessionChangeListener : SessionServiceDestructor | undefined;

    constructor(props: LoginViewProps) {

        super(props);

        this.state = {
            form: {
                username: '',
                password: ''
            },
            loginError: false,
            submitting: false,
            hasSession: false
        };

        this._loginFormModel = {
            "title": "Sign in",
            "items": [
                {
                    "type": FormItemType.EMAIL_FIELD,
                    "key": "username",
                    "label": "Username"
                },
                {
                    "type": FormItemType.PASSWORD_FIELD,
                    "key": "password",
                    "label": "Password"
                }
            ],
            "submitLabel": "Sign in"
        };

        this._changeFormCallback     = this._onFormChange.bind(this);
        this._submitFormCallback     = this._onLoginSubmit.bind(this);
        this._loginErrorCallback     = this._onLoginError.bind(this);
        this._sessionChangedCallback = this._onSessionChange.bind(this);

    }

    public componentDidMount (): void {

        this._loginErrorListener = SessionService.on(SessionServiceEvent.LOGIN_ERROR, this._loginErrorCallback);

        if ( this.props.hasSession === undefined ) {

            if (this.state.hasSession !== SessionService.hasSession()) {
                this.setState(() => ({hasSession: SessionService.hasSession()}));
            }

            if (this._sessionChangeListener === undefined) {
                this._startListening();
            }

        }

    }

    private _startListening () {
        this._sessionChangeListener = SessionService.on(SessionServiceEvent.CHANGED, this._sessionChangedCallback);
    }

    public componentDidUpdate (prevProps: Readonly<LoginViewProps>, prevState: Readonly<LoginViewState>, snapshot?: any): void {

        if (this.props.hasSession) {
            LOG.debug('Session detected: Closing login modal...');
            FormLoginModal.closeModal();
            return;
        }

        if (this.props.hasSession !== undefined) {

            if ( this._sessionChangeListener ) {
                this._sessionChangeListener();
                this._sessionChangeListener = undefined;
            }

            if (this.state.hasSession !== this.props.hasSession) {
                this.setState({hasSession: this.props.hasSession});
            }

        } else if (this._sessionChangeListener === undefined) {

            this._startListening();

        }

    }

    public componentWillUnmount (): void {

        if ( this._loginErrorListener ) {
            this._loginErrorListener();
            this._loginErrorListener = undefined;
        }

        if ( this._sessionChangeListener ) {
            this._sessionChangeListener();
            this._sessionChangeListener = undefined;
        }

    }

    public render () {

        return (
            <div className={FormAppClassName.LOGIN_VIEW
                + ' ' + (this.props.className ?? '')
            }>

                {this.props.hasSession ? (

                    <React.Fragment >

                        <h2>Welcome!</h2>

                        <p>You can manage forms now.</p>

                    </React.Fragment>

                ) : (

                    <React.Fragment >

                        {this.state.loginError ? (
                            <p><strong>Could not login.</strong> Try again.</p>
                        ) : null}

                        <Form
                            model={this._loginFormModel}
                            value={this.state.form}
                            change={this._changeFormCallback}
                            submit={this._submitFormCallback}
                        />

                    </React.Fragment>

                )}

            </div>
        );

    }


    private _onFormChange (value: LoginFormData) {

        this.setState({
            form: value
        });

    }

    private _onLoginSubmit () {

        const {username, password} = this.state.form;

        SessionService.login(username, password);

    }

    private _onLoginError () {

        if ( !this.state.loginError ) {
            this.setState({loginError: true});
        }

    }

    private _onSessionChange () {

        const hasSession = SessionService.hasSession();

        if (hasSession) {
            LOG.debug('_onSessionChange: Session detected: Closing login modal...');
            FormLoginModal.closeModal();
            return;
        }

        if (this.state.hasSession !== hasSession) {
            this.setState(() => ({hasSession: hasSession}));
        }

    }

}

export default LoginView;
